<template>
  <div class="home">
    <Hero 
      :title="heroTitle" 
      :subtitle="heroSubtitle"
      :imageUrl="heroImage" 
      :showActions="true"
      :showScrollIndicator="true"
      height="80vh"
    />
    
    <!-- Welcome Section -->
    <section class="welcome-section section">
      <div class="container">
        <div class="welcome-content">
          <h2 class="section-title">Welcome to China Stroll</h2>
          <p class="section-description">
            Discover the beauty and culture of China through our curated content. 
            Explore serene locations, immerse yourself in cultural experiences, 
            and enjoy captivating stories that bring the rich heritage of China to life.
          </p>
        </div>
      </div>
    </section>

    <!-- Featured Categories -->
    <section class="categories-section section-lg">
      <div class="container">
        <h2 class="section-title text-center">Discover China</h2>
        <p class="section-subtitle text-center">
          Journey through three unique perspectives of Chinese culture and landscapes
        </p>
        
        <div class="categories-grid">
          <div class="category-card" v-for="category in categories" :key="category.id">
            <router-link :to="category.path" class="category-link">
              <div class="category-image">
                <img :src="category.image" :alt="category.title" />
                <div class="category-overlay">
                  <div class="category-content">
                    <h3 class="category-title">{{ category.title }}</h3>
                    <p class="category-description">{{ category.description }}</p>
                    <span class="category-cta">
                      {{ category.cta }}
                      <svg class="arrow-icon" viewBox="0 0 24 24" fill="none">
                        <path d="M7 17L17 7M17 7H7M17 7V17" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                      </svg>
                    </span>
                  </div>
                </div>
              </div>
            </router-link>
          </div>
        </div>
      </div>
    </section>

    <!-- Stats Section -->
    <section class="stats-section section" style="background-color: var(--neutral-200);">
      <div class="container">
        <div class="stats-grid">
          <div class="stat-item" v-for="stat in stats" :key="stat.label">
            <div class="stat-number">{{ stat.number }}</div>
            <div class="stat-label">{{ stat.label }}</div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import Hero from '@/components/Hero.vue';
import heroImage from '@/assets/images/home-hero.jpg';
import exploreImage from '@/assets/images/explore-thumbnail.jpg';
import cultureImage from '@/assets/images/culture-thumbnail.jpg';
import storiesImage from '@/assets/images/stories-thumbnail.jpg';

export default {
  name: 'Home',
  components: {
    Hero,
  },
  data() {
    return {
      heroTitle: 'China Stroll',
      heroSubtitle: 'Journey through the heart of China with immersive stories, cultural insights, and breathtaking destinations',
      heroImage,
      categories: [
        {
          id: 'explore',
          title: 'Explore',
          description: 'Discover hidden gems and breathtaking landscapes across China',
          image: exploreImage,
          path: '/explore',
          cta: 'Start Exploring'
        },
        {
          id: 'culture',
          title: 'Culture',
          description: 'Immerse yourself in rich traditions and ancient wisdom',
          image: cultureImage,
          path: '/culture',
          cta: 'Discover Culture'
        },
        {
          id: 'stories',
          title: 'Stories',
          description: 'Read personal journeys and transformative experiences',
          image: storiesImage,
          path: '/stories',
          cta: 'Read Stories'
        }
      ],
      stats: [
        { number: '13+', label: 'Featured Articles' },
        { number: '3', label: 'Content Categories' },
        { number: '2025', label: 'Latest Updates' },
        { number: '∞', label: 'Discoveries Await' }
      ]
    };
  },
};
</script>

<style scoped>
/* Section Titles */
.section-title {
  font-family: var(--font-display);
  font-size: var(--text-4xl);
  font-weight: 600;
  color: var(--neutral-900);
  margin-bottom: var(--space-6);
  line-height: var(--leading-tight);
}

.section-subtitle {
  font-size: var(--text-lg);
  color: var(--neutral-600);
  margin-bottom: var(--space-12);
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
}

.section-description {
  font-size: var(--text-lg);
  color: var(--neutral-600);
  line-height: var(--leading-relaxed);
  max-width: 700px;
  margin: 0 auto;
}

/* Welcome Section */
.welcome-section {
  text-align: center;
}

.welcome-content {
  max-width: 800px;
  margin: 0 auto;
}

/* Categories Grid */
.categories-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(350px, 1fr));
  gap: var(--space-8);
  margin-top: var(--space-12);
}

.category-card {
  position: relative;
  border-radius: var(--radius-xl);
  overflow: hidden;
  box-shadow: var(--shadow-lg);
  transition: all var(--transition-normal);
}

.category-card:hover {
  transform: translateY(-8px);
  box-shadow: var(--shadow-xl);
}

.category-link {
  display: block;
  text-decoration: none;
}

.category-image {
  position: relative;
  height: 300px;
  overflow: hidden;
}

.category-image img {
  width: 100%;
  height: 100%;
  object-fit: cover;
  transition: transform var(--transition-slow);
}

.category-card:hover .category-image img {
  transform: scale(1.05);
}

.category-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(135deg, rgba(0, 0, 0, 0.4) 0%, rgba(0, 0, 0, 0.7) 100%);
  display: flex;
  align-items: end;
  padding: var(--space-6);
  color: var(--neutral-100);
  transition: background var(--transition-normal);
}

.category-card:hover .category-overlay {
  background: linear-gradient(135deg, rgba(196, 30, 58, 0.6) 0%, rgba(196, 30, 58, 0.8) 100%);
}

.category-content {
  width: 100%;
}

.category-title {
  font-family: var(--font-display);
  font-size: var(--text-2xl);
  font-weight: 600;
  margin-bottom: var(--space-2);
  color: var(--neutral-100);
}

.category-description {
  font-size: var(--text-base);
  margin-bottom: var(--space-4);
  opacity: 0.9;
  line-height: var(--leading-relaxed);
}

.category-cta {
  display: inline-flex;
  align-items: center;
  gap: var(--space-2);
  font-weight: 500;
  transition: gap var(--transition-fast);
}

.category-card:hover .category-cta {
  gap: var(--space-3);
}

.arrow-icon {
  width: 18px;
  height: 18px;
  transition: transform var(--transition-fast);
}

.category-card:hover .arrow-icon {
  transform: translate(4px, -4px);
}

/* Stats Section */
.stats-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: var(--space-8);
  text-align: center;
}

.stat-item {
  padding: var(--space-6);
}

.stat-number {
  font-family: var(--font-display);
  font-size: var(--text-4xl);
  font-weight: 700;
  color: var(--primary-color);
  margin-bottom: var(--space-2);
}

.stat-label {
  font-size: var(--text-base);
  color: var(--neutral-600);
  font-weight: 500;
}

/* Responsive Design */
@media (max-width: 1024px) {
  .categories-grid {
    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
    gap: var(--space-6);
  }
  
  .section-title {
    font-size: var(--text-3xl);
  }
}

@media (max-width: 768px) {
  .categories-grid {
    grid-template-columns: 1fr;
    gap: var(--space-6);
  }
  
  .category-image {
    height: 250px;
  }
  
  .section-title {
    font-size: var(--text-2xl);
  }
  
  .section-subtitle {
    font-size: var(--text-base);
  }
  
  .section-description {
    font-size: var(--text-base);
  }
  
  .stats-grid {
    grid-template-columns: repeat(2, 1fr);
    gap: var(--space-6);
  }
  
  .stat-number {
    font-size: var(--text-3xl);
  }
}

@media (max-width: 480px) {
  .categories-grid {
    gap: var(--space-4);
  }
  
  .category-overlay {
    padding: var(--space-4);
  }
  
  .category-title {
    font-size: var(--text-xl);
  }
  
  .category-description {
    font-size: var(--text-sm);
  }
  
  .stats-grid {
    grid-template-columns: 1fr;
    gap: var(--space-4);
  }
}
</style>